import { Box, GridLegacy as MuiGrid } from '@mui/material'

import React, { useEffect, useRef } from 'react'

import type { MenuItem } from '../../domain/menu-item'
import type { MenuVendor } from '../../domain/menu-vendor'
import MenuItemCard, { MemoizedMenuItemCardOptimized } from '../menu-item-card'
import { MenuItemSkeleton } from '../skeleton/MenuItemSkeleton'
import { ContentVisibilityRenderer } from './content-visibility-renderer'
import { useMenuCardColumns } from './useMenuCardColumns'

type Props = {
  vendor: MenuVendor
  taxAcronym: string
  loadingMore: boolean
  useVirtualisedList?: boolean
  onLoadMore?: () => void
  hasMore?: boolean
  menuItems: MenuItem[]
  useOptimizedCard?: boolean
}

export const MenuItemsRenderer: React.FC<Props> = ({
  vendor,
  menuItems,
  taxAcronym,
  loadingMore,
  useVirtualisedList,
  onLoadMore,
  hasMore,
  useOptimizedCard = false,
}) => {
  const { ref: menuRef, cardCols } = useMenuCardColumns()
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (useVirtualisedList || !hasMore || !onLoadMore) return
    const node = sentinelRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !loadingMore) {
            void onLoadMore()
          }
        })
      },
      { rootMargin: '200px' },
    )

    observer.observe(node)
    return () => {
      observer.disconnect()
    }
  }, [useVirtualisedList, hasMore, onLoadMore, loadingMore])

  const CardComponent = useOptimizedCard ? MemoizedMenuItemCardOptimized : MenuItemCard

  const renderNonVirtualized = (
    <Box sx={{ mb: 2 }}>
      <MuiGrid container spacing={1.5}>
        {menuItems.map((menuItem) => (
          <MuiGrid key={menuItem._id} item xs={12 / cardCols} sx={{ display: 'flex' }}>
            <CardComponent
              numberInCol={cardCols}
              taxAcronym={taxAcronym}
              item={menuItem}
              vendor={vendor}
            />
          </MuiGrid>
        ))}
      </MuiGrid>
    </Box>
  )

  const hasAnyItems = menuItems.length > 0

  if (!hasAnyItems) {
    return null
  }

  return (
    <Box ref={menuRef}>
      {useVirtualisedList ? (
        <ContentVisibilityRenderer
          vendor={vendor}
          menuItems={menuItems}
          taxAcronym={taxAcronym}
          cardCols={cardCols}
          loadingMore={loadingMore}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          useOptimizedCard={useOptimizedCard}
        />
      ) : (
        <>
          {renderNonVirtualized}
          {!useVirtualisedList && hasMore ? (
            <Box ref={sentinelRef} sx={{ height: 4 }} aria-hidden="true" />
          ) : null}
        </>
      )}

      {loadingMore ? <MenuItemSkeleton /> : null}
    </Box>
  )
}
